import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useProjectStore } from '../../stores/projectStore';
import { useToast } from '../../stores/toastStore';
import ProjectSideNav, { type NavItem, type AutoGenStatus } from './ProjectSideNav';
import { useTranslation } from 'react-i18next';
import EditableHeaderTitle from '../common/EditableHeaderTitle';

export type TabType = 'settings' | 'script' | 'episodes';

interface ProjectLayoutProps {
  children: React.ReactNode;
  activeTab: TabType;
  activeNavItem?: NavItem;
  onNavItemChange?: (item: NavItem) => void;
  autoGenStatus?: AutoGenStatus;
}

export default function ProjectLayout({
  children,
  activeTab,
  activeNavItem,
  onNavItemChange,
  autoGenStatus
}: ProjectLayoutProps) {
  const { t } = useTranslation(['project', 'common']);
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const { project, loading: projectLoading, loadProject, updateProjectName } = useProjectStore();
  const [isNameSaving, setIsNameSaving] = useState(false);

  useEffect(() => {
    if (projectId) {
      loadProject(projectId);
    }
  }, [projectId, loadProject]);

  const handleBack = () => {
    navigate('/projects');
  };

  const handleNameSave = async (name: string) => {
    if (!project) return;

    setIsNameSaving(true);
    try {
      await updateProjectName(name);
      toast.success(t('project:projectNameUpdated'));
    } catch (error) {
      toast.error(t('project:projectNameUpdateFailed'));
    } finally {
      setIsNameSaving(false);
    }
  };

  const handleNavItemChange = (item: NavItem) => {
    onNavItemChange?.(item);
  };

  return (
    <div className="h-screen bg-bg-page flex flex-col overflow-hidden">
      {/* Top Header */}
      <header className="h-14 flex items-center px-6 border-b border-border-subtle bg-bg-surface flex-shrink-0">
        <button
          onClick={handleBack}
          className="w-9 h-9 flex items-center justify-center text-gray-300 hover:text-gray-50 hover:bg-bg-elevated rounded-lg transition-colors flex-shrink-0 cursor-pointer"
        >
          <ArrowLeftIcon className="w-[18px] h-[18px]" />
        </button>
        <div className="ml-3">
          {projectLoading && !project ? (
            <div className="h-5 w-32 bg-bg-elevated animate-pulse rounded" />
          ) : (
            <EditableHeaderTitle
              title={project?.name || ''}
              fallbackTitle={t('project:unknownProject')}
              onSave={handleNameSave}
              isSaving={isNameSaving}
              titleClassName="text-base font-semibold text-gray-50"
              maxWidthClassName="max-w-[320px]"
            />
          )}
        </div>
      </header>

      {/* Body - Sidebar + Content */}
      <div className="flex-1 flex overflow-hidden">
        <aside className="w-56 flex-shrink-0 border-r border-border-subtle bg-bg-surface overflow-y-auto p-3">
          <ProjectSideNav
            activeTab={activeTab}
            activeNavItem={activeNavItem}
            onItemChange={handleNavItemChange}
            autoGenStatus={autoGenStatus}
          />
        </aside>

        <main className="flex-1 flex flex-col overflow-hidden">
          {children}
        </main>
      </div>
    </div>
  );
}
